import React from 'react'
import { createRoot } from 'react-dom/client'
import { ConfigProvider, theme as antdTheme } from 'antd'
import PatternFeedbackWidget from './PatternFeedbackWidget'
import { getThemeColors } from '../../../configs/theme.colors'

/**
 * Adds an inline pattern feedback widget to the CodeMirror editor
 * @param {object} editor - CodeMirror editor instance
 * @param {object} patternFeedback - Pattern feedback object containing name, feedback, grade, type, startRow, endRow
 * @returns {object} Object containing lineWidget and root for cleanup
 */
export const addPatternFeedbackWidget = (editor, patternFeedback) => {
    if (!editor || !patternFeedback) {
        return null
    }

    const { name, feedback, grade, type, startRow } = patternFeedback

    // Detect dark mode from the document's data-theme attribute
    const isDarkMode = document.documentElement.getAttribute('data-theme') === 'dark'
    const colors = getThemeColors(isDarkMode ? 'dark' : 'light')

    // Create container DOM element
    const container = document.createElement('div')
    container.className = 'pattern-feedback-widget-container'

    // Create root and render React component into container using React 18 API
    // Wrap with ConfigProvider to apply the same theme as the main app
    const root = createRoot(container)
    root.render(
        <ConfigProvider
            theme={{
                algorithm: isDarkMode ? antdTheme.darkAlgorithm : antdTheme.defaultAlgorithm,
                token: {
                    colorBgContainer: isDarkMode ? colors.secondBg : colors.mainBg,
                    colorText: colors.text,
                    colorBorder: colors.borderColor,
                },
            }}
        >
            <PatternFeedbackWidget name={name} feedback={feedback} grade={grade} type={type} />
        </ConfigProvider>
    )

    // Add line widget before the pattern's start line (startRow - 1 for 0-indexed)
    const lineWidget = editor.addLineWidget(startRow - 1, container, {
        coverGutter: false,
        noHScroll: false,
        above: true, // Show above the line (before the pattern starts)
    })

    setTimeout(() => {
        // First, scroll CodeMirror's own viewport to the feedback line so the
        // widget is actually laid out at its real position (CodeMirror only
        // renders lines in/near its visible area).
        if (typeof editor.scrollIntoView === 'function') {
            editor.scrollIntoView({ line: startRow - 1, ch: 0 }, 80)
        }

        const scrollParent = container.closest('.instant-feedback-review__card-content') || container.parentElement
        if (!scrollParent) return

        // Compute the container's position relative to the scroll parent using
        // bounding rects. We cannot use offsetTop here because the widget's
        // offsetParent is inside CodeMirror, not the scroll container, so the
        // raw offsetTop subtraction collapses to ~0 and always scrolls to top.
        const containerRect = container.getBoundingClientRect()
        const parentRect = scrollParent.getBoundingClientRect()

        const stickyHeader = scrollParent.querySelector('.instant-feedback-review__sticky-header')
        const headerOffset = stickyHeader ? stickyHeader.offsetHeight : 0

        const targetTop = scrollParent.scrollTop + (containerRect.top - parentRect.top) - headerOffset - 8
        scrollParent.scrollTo({ top: Math.max(0, targetTop), behavior: 'smooth' })
    }, 100)

    // Return both lineWidget and root for proper cleanup
    return { lineWidget, root }
}

/**
 * Removes a pattern feedback widget and unmounts React component
 * @param {object} widgetData - Object containing lineWidget and root instances
 */
export const removePatternFeedbackWidget = widgetData => {
    if (!widgetData) {
        return
    }

    const { lineWidget, root } = widgetData

    // Clear the line widget from CodeMirror
    if (lineWidget) {
        lineWidget.clear()
    }

    // Unmount React component using React 18 API
    if (root) {
        root.unmount()
    }
}

